import React, { useEffect, lazy } from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import debug from 'debug';
import Container from '../../components/Layout/Container';
import LoadingScreen from '../../components/Progress/LoadingScreen';
import CardConfigurer from '../../components/CardConfigurer/CardConfigurer';
import useNotification from 'effects/useNotification';
import HelmetHelper from 'components/Helmet/HelmetHelper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
// import ProductHeroBanner from 'components/Product/ProductHeroBanner';
import { cgtag } from 'utilities/gtag';
import { shopifyExtractId } from 'lib/shopifyHelpers';
import { useProduct } from 'effects/useProduct';
import useTransparentMenu from 'effects/useTransparentMenu';
import { productGetSplash } from 'lib/product';
import useStickyMenu from 'effects/useStickyMenu';
import DynamicCarousel from 'components/DynamicCarousel/DynamicCarousel';
import { productToSlideItem } from 'components/DynamicCarousel/libDynamicCarousel';

const DescriptionHelper = lazy(() => import('components/Layout/DescriptionHelper' /* webpackChunkName: "descriptionHelper" */));

const d = debug('productDetailsScreen');
let renderCount = 0;

interface MatchParams {
  productHandle: string;
  action?: string;
}

interface ProductDetailsScreenProps extends RouteComponentProps<MatchParams> {}

const ProductDetailsScreen: React.FC<ProductDetailsScreenProps> = ({ match }) => {
  const showMessage = useNotification();
  const productHandle = match.params.productHandle;
  const action = match.params.action;
  const { product, error: productError } = useProduct(productHandle);
  const history = useHistory();
  const productHasSplash = !!product && !!productGetSplash(product);
  useTransparentMenu(productHasSplash);
  useStickyMenu(!productHasSplash);

  d('renderProduct', renderCount++, product, productError, action);

  useEffect(() => {
    if (product) {
      d('viewItemAnalytics', product);
      cgtag('view_item', {
        ecommerce: {
          items: [
            {
              item_name: product.title, // Name or ID is required.
              item_id: shopifyExtractId(product.id.toString()),
              item_handle: product.handle,
              item_brand: product.vendor,
              item_category: product.productType,
            },
          ],
        },
      });
      // klaviyo
      var item = {
        Name: product.title,
        ProductID: product.id,
        Categories: [],
        ImageURL: product.featuredImage?.src,
        URL: '/product/' + product.handle,
        Brand: product.vendor,
      };
      // @ts-ignore
      const _learnq: any = window?.['_learnq'] || [];
      if (_learnq) {
        d('klaviyoTracked', item);
        _learnq.push(['track', 'Viewed Product', item]);
        _learnq.push([
          'trackViewedItem',
          {
            Title: item.Name,
            ItemId: item.ProductID,
            Categories: item.Categories,
            ImageUrl: item.ImageURL,
            Url: item.URL,
            Metadata: {
              Brand: item.Brand,
              // Price: item.Price,
              // CompareAtPrice: item.CompareAtPrice
            },
          },
        ]);
      }
    }
  }, [product]);

  useEffect(() => {
    if (productError && productError?.message === '404') {
      // redirect to 404...
      return history.replace('/404');
    }
    if (productError) {
      showMessage(productError.message, 'error');
    }
  }, [showMessage, productError, history]);

  if (!product) return <LoadingScreen />;

  return (
    <div key={productHandle}>
      <HelmetHelper
        //
        title={product.seo?.title || product.title}
        description={product.seo?.description || product.pd?.abstract || ''}
        canonical={`/product/${product.handle}/`}
        image={product.featuredImage?.src}
      />
      {action === 'configure' && <CardConfigurer product={product} initialStep={0} />}
      <DynamicCarousel
        displayMode="product"
        id={product.handle}
        content={{
          title: product.title,
          subtitle: product.pd?.abstract || '',
          // backgroundImage: collection?.image?.src,
          slides: [
            productToSlideItem(
              product,
              {},
              {
                buttonLabel: 'Buy Now',
                buttonTo: `/product/${product.handle}/configure`,
                title: product.title,
                subtitle: product.pd?.abstract || '',
                caption: undefined,
              },
            ),
          ],
        }}
      />
      <Container>
        <Box my={['40px', '40px']}>
          <Typography component="div">
            <DescriptionHelper html={product.description} />
          </Typography>
        </Box>
      </Container>
    </div>
  );
};

export default ProductDetailsScreen;
